import React, { useState } from 'react';
import { EyeIcon, EyeOffIcon, LockIcon, MailIcon, AlertTriangleIcon } from 'lucide-react';
interface LoginScreenProps {
  onLogin: () => void;
}
export function LoginScreen({
  onLogin
}: LoginScreenProps) {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);
    // Simulate a brief loading state
    setTimeout(() => {
      if (email === 'demo@cauri.co.uk' && password === '3thic@lB@nk') {
        onLogin();
      } else {
        setError('Invalid email or password. Please try again.');
        setIsLoading(false);
      }
    }, 800);
  };
  return <div className="min-h-screen bg-gray-900 flex flex-col" data-id="element-483">
      {/* Warning Banner */}
      <div className="bg-red-600 text-white py-3 px-4" data-id="element-484">
        <div className="max-w-7xl mx-auto flex items-center justify-center" data-id="element-485">
          <AlertTriangleIcon className="w-5 h-5 mr-2 flex-shrink-0" data-id="element-486" />
          <p className="text-sm font-medium" data-id="element-487">
            Demo is for User Experience testing only - No live data.
          </p>
        </div>
      </div>

      {/* Login Content */}
      <div className="flex-1 flex items-center justify-center p-4" data-id="element-488">
        <div className="w-full max-w-md" data-id="element-489">
          {/* Logo and Header */}
          <div className="text-center mb-8" data-id="element-490">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/9DSR6fPv4RouiZgYvtqGmX/Untitled-4-Photoroom.png" alt="Cauri Logo" className="h-12 mx-auto mb-6" data-id="element-491" />
            <h1 className="text-3xl font-bold text-white mb-2" data-id="element-492">
              Welcome to Cauri
            </h1>
            <p className="text-gray-400 text-sm" data-id="element-493">Bank with Purpose</p>
          </div>

          {/* Login Card */}
          <div className="bg-gray-800 rounded-2xl shadow-2xl p-8 border border-gray-700" data-id="element-494">
            <h2 className="text-2xl font-bold text-white mb-6" data-id="element-495">Sign In</h2>

            <form onSubmit={handleSubmit} className="space-y-5" data-id="element-496">
              {/* Email Input */}
              <div data-id="element-497">
                <label htmlFor="email" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-498">
                  Email Address
                </label>
                <div className="relative" data-id="element-499">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-500">
                    <MailIcon className="h-5 w-5 text-gray-500" data-id="element-501" />
                  </div>
                  <input id="email" type="email" value={email} onChange={e => setEmail(e.target.value)} className="block w-full pl-10 pr-3 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all placeholder-gray-500" placeholder="Enter your email" required data-id="element-502" />
                </div>
              </div>

              {/* Password Input */}
              <div data-id="element-503">
                <label htmlFor="password" className="block text-sm font-medium text-gray-300 mb-2" data-id="element-504">
                  Password
                </label>
                <div className="relative" data-id="element-505">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none" data-id="element-506">
                    <LockIcon className="h-5 w-5 text-gray-500" data-id="element-507" />
                  </div>
                  <input id="password" type={showPassword ? 'text' : 'password'} value={password} onChange={e => setPassword(e.target.value)} className="block w-full pl-10 pr-12 py-3 bg-gray-700 border border-gray-600 text-white rounded-lg focus:ring-2 focus:ring-primary focus:border-transparent transition-all placeholder-gray-500" placeholder="Enter your password" required data-id="element-508" />
                  <button type="button" onClick={() => setShowPassword(!showPassword)} className="absolute inset-y-0 right-0 pr-3 flex items-center" data-id="element-509">
                    {showPassword ? <EyeOffIcon className="h-5 w-5 text-gray-500 hover:text-gray-400" data-id="element-510" /> : <EyeIcon className="h-5 w-5 text-gray-500 hover:text-gray-400" data-id="element-511" />}
                  </button>
                </div>
              </div>

              {/* Error Message */}
              {error && <div className="bg-red-900/50 border border-red-700 text-red-200 px-4 py-3 rounded-lg text-sm" data-id="element-512">
                  {error}
                </div>}

              {/* Submit Button */}
              <button type="submit" disabled={isLoading} className="w-full bg-gradient-to-r from-primary to-secondary text-white py-3 px-4 rounded-lg font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 disabled:cursor-not-allowed" data-id="element-513">
                {isLoading ? <span className="flex items-center justify-center" data-id="element-514">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" data-id="element-515">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" data-id="element-516"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" data-id="element-517"></path>
                    </svg>
                    Signing in...
                  </span> : 'Sign In'}
              </button>
            </form>
          </div>

          {/* Footer */}
          <p className="text-center text-gray-500 text-xs mt-6" data-id="element-518">
            © 2025 Cauri. Ethical banking for everyone.
          </p>
        </div>
      </div>
    </div>;
}